import { addHours, addMonths, format, startOfDay, startOfMonth } from 'date-fns';
import type { Appointment } from '@/features/appointments/types/appointment.types';
import { parseLocalDate } from './timeUtils';

export interface CalendarEvent {
    id: string;
    title: string;
    start: Date;
    end: Date;
    resource: {
        type: 'booking' | 'blocked' | 'Google Meet';
        count: number;
        date: string;
    };
}

/**
 * Convert grouped appointment data to calendar events
 */
export function convertGroupedDataToEvents(
    groupedData: Record<string, { blockedTimes: number; appointments: number }>
): CalendarEvent[] {
    const events: CalendarEvent[] = [];

    Object.entries(groupedData).forEach(([dateString, counts]) => {
        // Parse date string as local date (YYYY-MM-DD format)
        // parseISO treats dates without time as midnight UTC, which can shift to previous day in negative timezones
        const date = parseLocalDate(dateString);
        const dayStart = startOfDay(date);

        if (counts.appointments > 0) {
            events.push({
                id: `appointments-${dateString}`,
                title: `${counts.appointments} Appointment${counts.appointments !== 1 ? 's' : ''}`,
                start: dayStart,
                end: addHours(dayStart, 1),
                resource: { type: 'booking', count: counts.appointments, date: dateString },
            });
        }

        if (counts.blockedTimes > 0) {
            events.push({
                id: `blocked-${dateString}`,
                title: `${counts.blockedTimes} Blocked Time${counts.blockedTimes !== 1 ? 's' : ''}`,
                start: addHours(dayStart, 1),
                end: addHours(dayStart, 2),
                resource: { type: 'blocked', count: counts.blockedTimes, date: dateString },
            });
        }
    });

    return events;
}

/**
 * Get appointments for a specific day
 */
export function getAppointmentsForDay(appointments: Appointment[], date: Date): Appointment[] {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;

    const dayAppointments = appointments.filter(apt => apt.date === dateString);

    // Remove Google Calendar events that conflict with actual appointments (duplicates)
    const bookingTimeSlots = new Set<string>();
    dayAppointments.forEach(apt => {
        if (apt.type === 'booking') {
            bookingTimeSlots.add(`${apt.startTime}-${apt.endTime}`);
        }
    });

    return dayAppointments.filter(apt => {
        // Filter out canceled appointments
        if (apt.status === 'canceled') return false;

        // Keep all non-Google Calendar events
        if (apt.type !== 'Google Meet') return true;

        // For Google Calendar events, only keep if they don't conflict with an appointment
        const timeSlotKey = `${apt.startTime}-${apt.endTime}`;
        return !bookingTimeSlots.has(timeSlotKey);
    });
}

/**
 * Get month date range for API calls
 */
export function getMonthRange(date: Date) {
    const monthStart = startOfMonth(date);
    const nextMonthStart = startOfMonth(addMonths(date, 1));

    return {
        startOfMonth: format(monthStart, 'yyyy-MM-dd'),
        endOfMonth: format(nextMonthStart, 'yyyy-MM-dd'), // Use first day of next month like Vue
    };
}
